// ============================================
// DRAWEE RISK (RISCO SACADO) TYPES
// ============================================

export interface UnderlyingReceivable {
    id: string;
    cedente: string;
    cnpj_cedente: string;
    sacado: string;
    cnpj_sacado: string;
    person_id?: string | null;
    emissao: string;
    vencimento: string;
    valor_vencimento: number;
    valor_desembolso: number;
    prazo_dias: number;
    taxa_am: number;
    tir_aa: number;
    tir_am: number;
    spread_aa: number;
    vpl: number;
    margem_vpl: number;
    duration: number;
}

export interface ExtractionPayload {
    operation_type: string;
    operation_number?: string;
    nominal_value: string;
    approved_value: string;
    expiration_date: string;
    emission_date: string;
    originator: string;
    credit_product_type_name: string;
    due_date: string;
    with_recourse: boolean;
    contract_built_status: number;
    operation_schema_id: number;
    status: number;
    participants: Array<{
        person_id: string | null;
        name: string;
        document: string;
        participant_type: string;
        is_primary: boolean;
    }>;
    operation_dictionary: {
        operation_number: string;
        emission_date: string;
        credit_product_type: string;
        program_name: string;
        file_hash?: string;
        total_amount: number;
        expiration_date: string;
        external_link: string;
        rebate_amount: number;
        guarantee_percentage: number;
        disbursement: {
            totalAmount: number;
            disbursements: Array<{ date: string; amount: number }>;
        };
        underlying_receivables: {
            items: UnderlyingReceivable[];
            total_vencimento: number;
            total_desembolso: number;
            quantity: number;
            cdi_aa: number;
        };
        files: { artefacts: string[]; contract_file: string };
        assignor_billing_address: {
            name: string;
            document_number: string;
            address_street: string;
            address_number: string;
            address_complement: string;
            address_neighborhood: string;
            address_city: string;
            address_state: string;
            address_zip_code: string;
        };
        contracts: { items: unknown[]; total: number; quantity: number };
        collaterals: {
            total_value: number;
            total_quantity: number;
            assets: {
                inventory: { items: unknown[]; total: number; quantity: number };
                machinery: { items: unknown[]; total: number; quantity: number };
                real_estate: { items: unknown[]; total: number; quantity: number };
                securities: { items: unknown[]; total: number; quantity: number };
            };
        };
        base_calculation?: {
            tir_annual: number | null;
            tir_monthly: number | null;
            spread_annual: number | null;
            vpl: number | null;
            vpl_margin: number | null;
            duration_days: number | null;
            funding_rate?: number | null;
        };
        financial_params?: {
            funding_rate: number | null;
            platform_fee_rate: number | null;
            grossup_tax_rate: number | null;
        };
        main_risk?: {
            uuid: string | null;
            name: string;
            document: string;
        };
    };
}

export interface ExtractionResponse {
    valid: boolean;
    program: string;
    mode: string;
    file_hash?: string;
    payload: ExtractionPayload;
}

export const FileStatus = {
    PENDING: 'pending',
    UPLOADING: 'uploading',
    SUCCESS: 'success',
    ERROR: 'error',
} as const satisfies Record<string, string>;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type FileStatus = typeof FileStatus[keyof typeof FileStatus];

export interface FileEntry {
    id: string;
    file: File;
    status: FileStatus;
    errorMessage?: string;
    response?: ExtractionResponse;
    unresolvedPersons?: string[];
    /** True enquanto os dados complementares (pessoas, endereço, risco principal) ainda estão sendo resolvidos após a extração. */
    isEnriching?: boolean;
    duplicateInfo?: {
        sequencial_number: number;
        credit_operation_id: string | number;
    };
    pdfBase64?: string;
}
